import { Column, Entity, JoinColumn, ManyToOne, OneToMany } from 'typeorm';
import { BaseEntity } from './base.entity';
import { TeamClientRuleEntity } from './team-client-rule.entity';
import { TeamClientEntity } from './team-client.entity';
import { TeamGoalEntity } from './team-goal.entity';
import { TeamPreferenceEntity } from './team-preference.entity';
import { TeamTypeEntity } from './team-type.entity';
import { TeamUserEntity } from './team-user.entity';

@Entity('team')
export class TeamEntity extends BaseEntity {
	@Column('varchar', { name: 'name', length: 255 })
	public name!: string;

	@Column('varchar', { name: 'externalId', nullable: true, length: 64 })
	public externalId!: string | null;

	/**
	 * PD-16309 — the parent team. Teams nest to any depth, so "East Coast"
	 * can contain "Northeast" and "Southeast".
	 *
	 * A coverage role held on a parent covers every team beneath it. Null means
	 * this team sits at the top, which is where every existing team starts —
	 * the column is additive and nothing is backfilled.
	 */
	@Column('int', { name: 'fk_team__id_parent', nullable: true, unsigned: true })
	public fkTeamIdParent!: number | null;

	@ManyToOne(() => TeamEntity, (team) => team.childTeams, {
		onDelete: 'SET NULL',
		nullable: true,
	})
	@JoinColumn([{ name: 'fk_team__id_parent', referencedColumnName: 'id' }])
	public parentTeam!: TeamEntity | null;

	@OneToMany(() => TeamEntity, (team) => team.parentTeam, {})
	public childTeams!: Array<TeamEntity>;

	/** PD-16309 — classification only; it grants nothing. */
	@Column('int', { name: 'fk_team_type__id', nullable: true, unsigned: true })
	public fkTeamTypeId!: number | null;

	@ManyToOne(() => TeamTypeEntity, (teamType) => teamType.teams, {
		onDelete: 'SET NULL',
		nullable: true,
	})
	@JoinColumn([{ name: 'fk_team_type__id', referencedColumnName: 'id' }])
	public fkTeamType!: TeamTypeEntity | null;

	@OneToMany(() => TeamGoalEntity, (teamGoal) => teamGoal.fkTeam, {})
	public teamGoals!: Array<TeamGoalEntity>;

	@OneToMany(
		() => TeamPreferenceEntity,
		(teamPreference) => teamPreference.fkTeam,
		{},
	)
	public teamPreferences!: Array<TeamPreferenceEntity>;

	@OneToMany(() => TeamUserEntity, (teamUser) => teamUser.fkTeam, {})
	public teamUsers!: Array<TeamUserEntity>;

	/** PD-16310 — the criteria deciding which clients this team covers. */
	@OneToMany(() => TeamClientRuleEntity, (rule) => rule.fkTeam, {})
	public clientRules!: Array<TeamClientRuleEntity>;

	/** PD-16310 — the materialized result of those criteria, plus manual adds. */
	@OneToMany(() => TeamClientEntity, (teamClient) => teamClient.fkTeam, {})
	public teamClients!: Array<TeamClientEntity>;
}
